import { AxiosError } from 'axios';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import { actionLog } from "services/actionlog.service";
import { fetchUser, signInRequest } from 'services/auth.service';
import {
  // updateUserProfile,
  updateUserStatus,
  // uploadPicture,
} from 'services/profile.service';
import { clearFlowSessionState } from 'pages/MinhaEquipe/ApreciarRealizacao/shared/eligibility';
import { CyclesInterface, RealCycle, User, UserInfoInterface } from 'types/user';
import { clearStorage, getStorageItem, removeStorageItem, setStorageItem } from 'utils/storage';

import { api } from '../../services/api';
import { setAuthorizationHeader } from '../../services/interceptors';
import { createTokenCookies, getToken, removeTokenCookies } from '../../utils/tokenCookies';
import { getCyclesExtended, getPlayerInfo, getRealCycles, getTaskCycles } from 'services/dashboard.service';
import { GetVersion } from 'services/utils';
import { ActionLog } from 'types/funifier';
import { getActiveGame } from 'services/actionlog.service';

interface SignInCredentials {
  username: string;
  password: string;
  profile?: { url: string };
}

interface AuthContextData {
  login: (credentials: SignInCredentials) => Promise<void | AxiosError>;
  loginWithToken: (access_token: string) => Promise<void>;
  signOut: (pathname?: string) => void;
  refreshUser: () => void;
  refreshUserPromise: () => Promise<void>;
  user?: User;
  userInfo: UserInfoInterface | undefined;
  isAuthenticated: boolean;
  loadingUserData: boolean;
  isMSTeams: boolean;
  cycles: CyclesInterface[] | undefined;
  currentCycle: CyclesInterface | undefined;
  realCycle: RealCycle[] | undefined;
  cyclesExtended: RealCycle[] | undefined;
  setRealCycle: (data?: any) => void;
  cycleSelected: { _id: string; title: string; isV2?: boolean; version: string } | undefined;
  setCycleSelected: (data?: any) => void;
  showNotifications: boolean;
  setShowNotifications: (data: boolean) => void;
  activeGame: { drawGame?: string | undefined; played: boolean; interaction: boolean } | undefined;
  setActiveGame: (data: { drawGame?: string | undefined; played: boolean; interaction: boolean } | undefined) => void;
  loadGame: () => Promise<void>;
  loadingInfo: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const token = getToken();

  const isAuthenticated = !!token;

  const [user, setUser] = useState<User | undefined>();
  const [userInfo, setUserInfo] = useState<UserInfoInterface | undefined>();
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [cycles, setCycles] = useState<CyclesInterface[] | undefined>();
  const [realCycle, setRealCycle] = useState<RealCycle[] | undefined>();
  const [cyclesExtended, setCyclesExtended] = useState<RealCycle[] | undefined>();
  const [isMSTeams, setIsMSTeams] = useState<boolean>(false);
  const [cycleSelected, setCycleSelected] = useState<{ _id: string; title: string; version: string } | undefined>();
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeGame, setActiveGame] = useState<{ drawGame?: string | undefined; played: boolean; interaction: boolean }>();
  const [loadingInfo, setLoadingInfo] = useState(false);

  const currentCycle = useMemo(() => {
    if (!cycles) return undefined;
    const cycle = cycles.find((item: any) => item.status === 'open');

    return !!cycle ? cycle : cycles[0];
  }, [cycles]);

  const signOut = useCallback((pathname?: string) => {
    removeTokenCookies();
    clearStorage();
    // clearStorage só limpa o localStorage; a escolha de fluxo/caches da apreciação
    // vivem em sessionStorage (navegação ativa) e precisam de limpeza explícita —
    // o logout MSAL mantém a aba viva e outro usuário poderia herdar a escolha.
    clearFlowSessionState();
    setAuthorizationHeader(api.defaults, '');
    setUser(undefined);
    setUserInfo(undefined);
    setRealCycle(undefined);
    setCyclesExtended(undefined);
    setCycles(undefined);
    setLoadingUserData(false);
    //   navigate(pathname || '/login');
  }, []);

  async function login({ username, password }: SignInCredentials) {
    try {
      const auth = await signInRequest({ username, password });

      if (auth.message) {
        throw auth;
      }

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('quizV2Minimized');
      }

      // Login novo = sessão nova de apreciação: não herda escolha de fluxo/caches de
      // elegibilidade de um usuário anterior na mesma aba.
      clearFlowSessionState();

      const { access_token, refreshToken, permissions, roles } = auth;

      createTokenCookies(access_token, refreshToken);
      setAuthorizationHeader(api.defaults, access_token);

      const _user = await fetchUser();
      const _userInfo = await getPlayerInfo(_user._id);

      setUserInfo(_userInfo);

      const userData = { _id: _user._id, ..._user, permissions, roles };

      setUser(userData);

      return userData;
    } catch (error) {
      const err = error as AxiosError;
      console.log(err);
      throw err;
    }
  }

  async function loginWithToken(access_token: string) {
    try {
      if (!access_token) {
        return;
      }

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem('quizV2Minimized');
      }

      // Login novo (SSO) = sessão nova de apreciação: não herda escolha de fluxo/caches.
      clearFlowSessionState();

      createTokenCookies(access_token, '');
      setAuthorizationHeader(api.defaults, access_token);

      const _user = await fetchUser();
      const _userInfo = await getPlayerInfo(_user._id);

      setUserInfo(_userInfo);

      const { name, image, extra } = _user;

      const userData = {
        _id: _user._id,
        ..._user,
        extra,
        name,
        image,
        permissions: null,
        roles: null,
      };

      setUser(userData);

      return userData;
    } catch (error) {
      const err = error as AxiosError;
      // console.log(err);
      throw err;
    }
  }

  async function refreshUser() {
    const updatedUser = await updateUserStatus();
    const _userInfo = await getPlayerInfo(updatedUser._id);

    setUserInfo(_userInfo);
    setUser(updatedUser);
  }

  const originalRefreshUser = refreshUser;
  const refreshUserPromise = async function () {
    return originalRefreshUser();
  };

  // useEffect(() => {
  // 	if (!token) signOut(pathname);
  // }, [pathname, token, signOut]);

  useEffect(() => {
    const token = getToken();

    async function getUserData() {
      setLoadingUserData(true);

      try {
        const response = await fetchUser();

        if (response) {
          const { _id, name, image, email, permissions, roles } = response;
          const _userInfo = await getPlayerInfo(_id);

          setUserInfo(_userInfo);
          setUser({ ...response, _id, name, image, email, permissions, roles });
        }
      } catch (error) {
        signOut();
      }

      setLoadingUserData(false);
    }

    if (!user && token) {
      setAuthorizationHeader(api.defaults, token);
      getUserData();
    }

    function handleSignoutEvent() {
      signOut();
    }

    document.addEventListener('signout', handleSignoutEvent, false);

    const _MSTeams = getStorageItem('isMSTeams');

    if (!!_MSTeams && _MSTeams === 'true') {
      setIsMSTeams(true);
    }

    return () => {
      document.removeEventListener('signout', handleSignoutEvent);
    };
  }, [user, signOut]);

  useEffect(() => {
    async function load() {
      const taskCycleResults = await getTaskCycles();
      setCycles(taskCycleResults);

      const current = new Date();
      const listRealCycle = await getRealCycles();
      const listCyclesExtended = await getCyclesExtended();

      const cycles = listRealCycle.filter((item: any) => {
        return item.date_start <= current.getTime() && item.year && item.year >= 2023;
      });

      const _cyclesExtended = listCyclesExtended.filter((item: any) => {
        return item.date_start <= current.getTime() && item.year && item.year >= 2023;
      });

      setRealCycle(cycles);
      setCyclesExtended(_cyclesExtended);

      if (Array.isArray(cycles) && cycles.length) {
        const cycle = !!cycles.find((item: any) => item.status === 'open')
          ? cycles.find((item: any) => item.status === 'open')
          : cycles[0];

        setCycleSelected({
          _id: cycle._id,
          title: cycle.title,
          version: GetVersion(cycle.date_start),
        });
      }
    }

    if (!!user && !cycles) {
      load();
    }
  }, [cycles, user]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sender = searchParams.get('invite');
    const teamsContext = searchParams.get('teamsContext');

    if (!!sender?.match(/^F\d+$/gi)) {
      setStorageItem('jogajuntobb_invite_code', sender);
    }

    if (!!teamsContext && teamsContext === 'true') {
      setStorageItem('isMSTeams', 'true');
      setIsMSTeams(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!!cycleSelected && !!cycleSelected.version && cycleSelected.version === 'v2') {
      document.body.classList.add('bg-v2');
    } else {
      document.body.classList.remove('bg-v2');
    }

    async function load() {
      setLoadingInfo(true);
      const _userInfo = await getPlayerInfo(user?._id, cycleSelected?.title);
      setUserInfo(_userInfo);
      setLoadingInfo(false);
    }

    if (user?._id) load();
  }, [cycleSelected, user?._id]);

  useEffect(() => {
    if (user && !activeGame) loadGame();
  }, [activeGame, user]);

  async function loadGame() {
    setActiveGame(undefined);

    // setActiveGame({drawGame: 'raspadinha', played: false, interaction: true}); // local teste, comenta as duas abaixo
    const game = await getActiveGame();
    setActiveGame(game);
  }

  return (
    <AuthContext.Provider
      value={{
        refreshUserPromise,
        isAuthenticated,
        user,
        userInfo,
        loadingUserData,
        cycles,
        realCycle,
        cyclesExtended,
        setRealCycle,
        currentCycle,
        isMSTeams,
        login,
        loginWithToken,
        signOut,
        refreshUser,
        cycleSelected,
        setCycleSelected,
        showNotifications,
        setShowNotifications,
        activeGame,
        setActiveGame,
        loadGame,
        loadingInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
